import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/cn.js";

/**
 * Two-column form layout used by every detail page (Designs, Blogs, Display
 * Homes, etc.). Per Tech Impl § 9 task 2 — main fields on the left, a sidebar
 * for status / featured / sortOrder / SEO on the right.
 *
 * Usage:
 *   <FormShell
 *     title="Edit Cromwell 25"
 *     backTo="/designs"
 *     primaryAction={<Button onClick={...}>Save</Button>}
 *     secondaryAction={<Button variant="secondary" onClick={...}>Cancel</Button>}
 *     sidebar={<>...status, featured, seo, audit fields...</>}
 *   >
 *     <Card><CardBody>...main field groups...</CardBody></Card>
 *   </FormShell>
 */
export function FormShell({
  title,
  description,
  backTo,
  backLabel = "Back",
  primaryAction,
  secondaryAction,
  sidebar,
  children,
  onSubmit,
  className,
}) {
  const Container = onSubmit ? "form" : "div";
  const containerProps = onSubmit ? { onSubmit, noValidate: true } : {};
  return (
    <Container {...containerProps} className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          {backTo && (
            <Link
              to={backTo}
              className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-accent"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {backLabel}
            </Link>
          )}
          <h1 className="font-display text-2xl text-text-primary">{title}</h1>
          {description && <p className="text-sm text-text-secondary">{description}</p>}
        </div>
        {(primaryAction || secondaryAction) && (
          <div className="flex items-center gap-2">
            {secondaryAction}
            {primaryAction}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">{children}</div>
        {sidebar && (
          <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start">{sidebar}</aside>
        )}
      </div>

      {/* Sticky footer for the primary action — duplicates the header buttons
          so save/cancel is reachable on long forms without scrolling. */}
      {(primaryAction || secondaryAction) && (
        <div className="flex items-center justify-end gap-2 border-t border-border-subtle pt-6">
          {secondaryAction}
          {primaryAction}
        </div>
      )}
    </Container>
  );
}
